import { inject, Pipe, type PipeTransform, SecurityContext } from '@angular/core';
import { DomSanitizer, type SafeValue } from '@angular/platform-browser';

@Pipe({
    name: 'sanitize',
})
export class SanitizePipe implements PipeTransform {
    private readonly sanitizer =  inject(DomSanitizer);

    private readonly dangerousSchemes = [
        // eslint-disable-next-line sonarjs/code-eval
        'javascript:',
        'data:',
        'vbscript:',
        'file:',
        'about:',
        'chrome:',
        'moz-extension:',
        'chrome-extension:',
        'unsafe:',
        'blob:',
        'view-source:',
    ];

    public transform(value: string, context: SecurityContext): SafeValue {
        switch (context) {
            case SecurityContext.RESOURCE_URL: {
                const sanitizedUrl = this.sanitizeUrl(value);

                // eslint-disable-next-line sonarjs/no-angular-bypass-sanitization
                return this.sanitizer.bypassSecurityTrustResourceUrl(sanitizedUrl);
            }
            case SecurityContext.HTML: {
                const sanitizedHtml = this.sanitizeHtml(value);

                // eslint-disable-next-line sonarjs/no-angular-bypass-sanitization
                return this.sanitizer.bypassSecurityTrustHtml(sanitizedHtml);
            }
            default:
                throw new Error(`Not supported SecurityContext '${context}'`);
        }
    }

    private sanitizeUrl(url: string): string {
        if (!url) {
            return '';
        }

        const lowerUrl = url.toLowerCase();
        for (const scheme of this.dangerousSchemes) {
            if (lowerUrl.startsWith(scheme)) {
                return '';
            }
        }

        return url;
    }

    private sanitizeHtml(html: string): string {
        if (!html) {
            return '';
        }

        // Check if the HTML contains SVG elements
        const hasSvg = /<svg[^>]*>/i.test(html);

        if (hasSvg) {
            // For SVG content, use our custom sanitization without Angular's sanitizer
            // This prevents Angular from stripping SVG elements entirely
            let sanitized = html;

            sanitized = this.removeDangerousElements(sanitized);
            sanitized = this.removeDangerousAttributes(sanitized);
            sanitized = this.removeDangerousUrls(sanitized);

            return sanitized;
        } else {
            // For non-SVG content, use Angular's built-in sanitizer first
            let sanitized = this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';

            sanitized = this.removeDangerousElements(sanitized);
            sanitized = this.removeDangerousAttributes(sanitized);
            sanitized = this.removeDangerousUrls(sanitized);

            return sanitized;
        }
    }

    private removeDangerousElements(html: string): string {
        // Only remove the most dangerous elements that can execute code
        const dangerousElements = [
            'script',
            'iframe',
            'object',
            'embed',
            'form',
            'meta',
            'base',
            'link',
        ];

        let sanitized = html;

        for (const element of dangerousElements) {
            // Remove opening tags
            sanitized = sanitized.replace(new RegExp(`<${element}[^>]*>`, 'gi'), '');
            // Remove closing tags
            sanitized = sanitized.replace(new RegExp(`</${element}>`, 'gi'), '');
        }

        return sanitized;
    }

    private removeDangerousAttributes(html: string): string {
        // Only remove event handlers and dangerous attributes
        const dangerousAttributes = [
            // Event handlers - be more specific to avoid matching parts of words
            '\\s+on\\w+\\s*=\\s*["\'][^"\']*["\']',
            // Dangerous CSS expressions
            'expression\\s*\\(',
            'behavior\\s*:',
            'binding\\s*:',
        ];

        let sanitized = html;

        for (const pattern of dangerousAttributes) {
            sanitized = sanitized.replace(new RegExp(pattern, 'gi'), '');
        }

        // Remove dangerous SVG attributes that could execute code
        sanitized = this.removeDangerousSvgAttributes(sanitized);

        return sanitized;
    }

    private removeDangerousSvgAttributes(html: string): string {
        // Remove dangerous SVG attributes that could execute code
        const dangerousSvgAttributes = [
            // SVG event handlers
            '\\s+on\\w+\\s*=\\s*["\'][^"\']*["\']',
            // SVG script references
            '\\s+href\\s*=\\s*["\'][^"\']*javascript:[^"\']*["\']',
            // SVG external references that could be dangerous
            '\\s+xlink:href\\s*=\\s*["\'][^"\']*javascript:[^"\']*["\']',
            // SVG foreignObject (can contain HTML/scripts)
            '<foreignObject[^>]*>',
            '</foreignObject>',
            // SVG script elements
            '<script[^>]*>',
            '</script>',
        ];

        let sanitized = html;

        for (const pattern of dangerousSvgAttributes) {
            sanitized = sanitized.replace(new RegExp(pattern, 'gi'), '');
        }

        return sanitized;
    }

    private removeDangerousUrls(html: string): string {
        // Only check href and src attributes for dangerous URLs
        const urlAttributes = ['href', 'src'];

        let sanitized = html;

        for (const attr of urlAttributes) {
            for (const scheme of this.dangerousSchemes) {
                const pattern = new RegExp(
                    // eslint-disable-next-line no-useless-escape
                    `${attr}\\s*=\\s*["\'][^"\']*${this.escapeRegex(scheme)}[^"\']*["\']`,
                    'gi',
                );
                sanitized = sanitized.replace(pattern, `${attr}=""`);
            }
        }

        return sanitized;
    }

    private escapeRegex(string: string): string {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
}
